import React, { useState, useCallback } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
  type DragOverEvent,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, X, Plus, RotateCcw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Modal, Button, Badge } from '../shared';
import { WIDGET_MAP, type DashboardWidgetDef } from './dashboardWidgetRegistry';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  visibleIds: string[];
  availableIds: string[];
  isPinned: (id: string) => boolean;
  onSave: (newWidgetIds: string[]) => void;
  onReset: () => void;
}

// ─── Droppable zone wrapper ─────────────────────────────────

function DroppableZone({ id, children }: { id: string; children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      className={`
        min-h-[60px] rounded-card p-2 transition-colors duration-200
        ${isOver ? 'bg-accent-soft/30' : ''}
      `}
    >
      {children}
    </div>
  );
}

// ─── Sortable visible item ─────────────────────────────────

function SortableWidgetItem({
  widget,
  pinned,
  label,
  sizeLabel,
  onRemove,
}: {
  widget: DashboardWidgetDef;
  pinned: boolean;
  label: string;
  sizeLabel: string;
  onRemove: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: widget.id, disabled: pinned });

  const Icon = widget.icon;
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`
        flex items-center gap-2.5 px-3 py-2 rounded-input
        bg-bg-surface border border-subtle
        transition-all duration-150
        ${isDragging ? 'opacity-40 scale-[0.98]' : ''}
        ${pinned ? 'opacity-70' : ''}
      `}
    >
      <button
        className={`shrink-0 touch-none ${pinned ? 'cursor-not-allowed text-txt-muted' : 'cursor-grab active:cursor-grabbing text-txt-tertiary hover:text-txt-secondary'}`}
        {...attributes}
        {...listeners}
        tabIndex={pinned ? -1 : 0}
      >
        <GripVertical className="w-4 h-4" strokeWidth={1.5} />
      </button>
      <Icon className="w-4.5 h-4.5 shrink-0 text-txt-secondary" strokeWidth={1.5} />
      <span className="flex-1 text-[13px] font-medium text-txt truncate">
        {label}
      </span>
      <Badge variant="neutral" className="text-[10px] shrink-0">
        {sizeLabel}
      </Badge>
      {!pinned && (
        <button
          onClick={onRemove}
          className="shrink-0 w-6 h-6 rounded-pill grid place-items-center text-txt-muted hover:text-danger hover:bg-danger-soft transition-colors duration-150"
        >
          <X className="w-3.5 h-3.5" strokeWidth={2} />
        </button>
      )}
    </div>
  );
}

// ─── Overlay item (while dragging) ─────────────────────────

function DragOverlayItem({ widget, label }: { widget: DashboardWidgetDef; label: string }) {
  const Icon = widget.icon;
  return (
    <div className="flex items-center gap-2.5 px-3 py-2 rounded-input bg-bg-card border border-accent shadow-heavy">
      <GripVertical className="w-4 h-4 text-accent shrink-0" strokeWidth={1.5} />
      <Icon className="w-4.5 h-4.5 shrink-0 text-accent" strokeWidth={1.5} />
      <span className="text-[13px] font-medium text-txt">{label}</span>
    </div>
  );
}

// ─── Available item (click to add) ─────────────────────────

function AvailableWidgetItem({
  widget,
  label,
  description,
  onAdd,
}: {
  widget: DashboardWidgetDef;
  label: string;
  description: string;
  onAdd: () => void;
}) {
  const Icon = widget.icon;
  return (
    <button
      onClick={onAdd}
      className="
        w-full flex items-center gap-2.5 px-3 py-2 rounded-input
        bg-bg-surface/50 border border-subtle
        text-txt-secondary hover:text-txt hover:bg-bg-surface hover:border-strong
        transition-all duration-150 cursor-pointer
        group
      "
    >
      <Icon className="w-4.5 h-4.5 shrink-0" strokeWidth={1.5} />
      <div className="flex-1 text-left min-w-0">
        <span className="block text-[13px] font-medium truncate">{label}</span>
        <span className="block text-[11px] text-txt-muted truncate">{description}</span>
      </div>
      <Plus className="w-4 h-4 shrink-0 text-txt-muted group-hover:text-accent transition-colors" strokeWidth={1.5} />
    </button>
  );
}

// ─── Main Modal ─────────────────────────────────────────────

const DashboardCustomizeModal: React.FC<Props> = ({
  isOpen,
  onClose,
  visibleIds: initialVisibleIds,
  availableIds: initialAvailableIds,
  isPinned,
  onSave,
  onReset,
}) => {
  const { t } = useTranslation('dashboard');
  const [localVisible, setLocalVisible] = useState<string[]>(initialVisibleIds);
  const [activeId, setActiveId] = useState<string | null>(null);

  React.useEffect(() => {
    if (isOpen) {
      setLocalVisible(initialVisibleIds);
    }
  }, [isOpen, initialVisibleIds]);

  const localAvailable = React.useMemo(() => {
    const visSet = new Set(localVisible);
    return initialAvailableIds
      .concat(initialVisibleIds.filter(id => !visSet.has(id)))
      .filter(id => !visSet.has(id) && WIDGET_MAP.has(id));
  }, [localVisible, initialAvailableIds, initialVisibleIds]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  }, []);

  const handleDragOver = useCallback((event: DragOverEvent) => {
    const { active, over } = event;
    if (!over) return;

    const activeItemId = active.id as string;
    const overId = over.id as string;

    const isActiveInVisible = localVisible.includes(activeItemId);
    const isOverVisible = overId === 'visible-zone' || localVisible.includes(overId);

    if (!isActiveInVisible && isOverVisible) {
      setLocalVisible(prev => {
        if (prev.includes(activeItemId)) return prev;
        const overIndex = prev.indexOf(overId);
        if (overIndex >= 0) {
          const next = [...prev];
          next.splice(overIndex + 1, 0, activeItemId);
          return next;
        }
        return [...prev, activeItemId];
      });
    }
  }, [localVisible]);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const activeItemId = active.id as string;
    const overId = over.id as string;

    if (localVisible.includes(activeItemId) && localVisible.includes(overId)) {
      setLocalVisible(prev => {
        const oldIdx = prev.indexOf(activeItemId);
        const newIdx = prev.indexOf(overId);
        if (oldIdx === newIdx) return prev;
        return arrayMove(prev, oldIdx, newIdx);
      });
    }
  }, [localVisible]);

  const handleRemove = useCallback((id: string) => {
    setLocalVisible(prev => prev.filter(x => x !== id));
  }, []);

  const handleAdd = useCallback((id: string) => {
    setLocalVisible(prev => (prev.includes(id) ? prev : [...prev, id]));
  }, []);

  const handleSave = useCallback(() => {
    onSave(localVisible);
    onClose();
  }, [localVisible, onSave, onClose]);

  const handleReset = useCallback(() => {
    onReset();
    onClose();
  }, [onReset, onClose]);

  const activeWidget = activeId ? WIDGET_MAP.get(activeId) : null;

  const getLabel = (id: string) => t(`customize.widgets.${id}.name`, id);
  const getDescription = (id: string) => t(`customize.widgets.${id}.description`, '');
  const getSizeLabel = (id: string) => {
    const w = WIDGET_MAP.get(id);
    return w?.size === 'full' ? t('customize.sizeFull') : t('customize.sizeHalf');
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('customize.title')}
      size="lg"
      footer={
        <div className="flex items-center justify-between w-full">
          <Button variant="ghost" onClick={handleReset} icon={<RotateCcw className="w-4 h-4" strokeWidth={1.5} />}>
            {t('customize.reset')}
          </Button>
          <div className="flex items-center gap-2">
            <Button variant="tonal" onClick={onClose}>
              {t('customize.cancel')}
            </Button>
            <Button variant="primary" onClick={handleSave}>
              {t('customize.save')}
            </Button>
          </div>
        </div>
      }
    >
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Visible widgets */}
          <div>
            <p className="font-mono text-[10px] font-medium text-txt-tertiary uppercase tracking-[0.08em] mb-2">
              {t('customize.visible')}
            </p>
            <p className="text-[12px] text-txt-muted mb-3">
              {t('customize.visibleHint')}
            </p>
            <DroppableZone id="visible-zone">
              <SortableContext items={localVisible} strategy={verticalListSortingStrategy}>
                <div className="flex flex-col gap-1.5">
                  {localVisible.map(id => {
                    const widget = WIDGET_MAP.get(id);
                    if (!widget) return null;
                    return (
                      <SortableWidgetItem
                        key={id}
                        widget={widget}
                        pinned={isPinned(id)}
                        label={getLabel(id)}
                        sizeLabel={getSizeLabel(id)}
                        onRemove={() => handleRemove(id)}
                      />
                    );
                  })}
                  {localVisible.length === 0 && (
                    <p className="text-[12px] text-txt-muted text-center py-4">
                      {t('customize.emptyVisible')}
                    </p>
                  )}
                </div>
              </SortableContext>
            </DroppableZone>
          </div>

          {/* Available widgets */}
          <div>
            <p className="font-mono text-[10px] font-medium text-txt-tertiary uppercase tracking-[0.08em] mb-2">
              {t('customize.available')}
            </p>
            <p className="text-[12px] text-txt-muted mb-3">
              {t('customize.availableHint')}
            </p>
            <div className="flex flex-col gap-1.5">
              {localAvailable.map(id => {
                const widget = WIDGET_MAP.get(id);
                if (!widget) return null;
                return (
                  <AvailableWidgetItem
                    key={id}
                    widget={widget}
                    label={getLabel(id)}
                    description={getDescription(id)}
                    onAdd={() => handleAdd(id)}
                  />
                );
              })}
              {localAvailable.length === 0 && (
                <p className="text-[12px] text-txt-muted text-center py-4">
                  {t('customize.emptyAvailable')}
                </p>
              )}
            </div>
          </div>
        </div>

        <DragOverlay>
          {activeWidget ? <DragOverlayItem widget={activeWidget} label={getLabel(activeWidget.id)} /> : null}
        </DragOverlay>
      </DndContext>
    </Modal>
  );
};

export default DashboardCustomizeModal;
